import { useScrollReveal } from '@/hooks/useScrollReveal';

const earningTiers = [
  {
    label: 'Per-Tonne Freezing Fee',
    desc: 'Earn a standardised fee for every tonne of fresh produce frozen at your facility. Rates vary by technology (IQF commands a premium).',
    icon: 'ri-money-rupee-circle-line',
  },
  {
    label: 'Per-Tonne-Month Storage Fee',
    desc: 'Recurring monthly revenue for every tonne held in cold storage. Longer holding periods compound your earnings.',
    icon: 'ri-calendar-check-line',
  },
  {
    label: 'High-Utilisation Bonus',
    desc: 'Maintain above 80% utilisation for 3 consecutive months and unlock an additional revenue-share tier on top of base rates.',
    icon: 'ri-star-line',
  },
];

const certTiers = [
  { label: 'Verified', desc: 'Basic facility verification — location, capacity, freezing technology confirmed by Freshhlock auditors.', icon: 'ri-shield-check-line', color: 'border-l-foreground-400' },
  { label: 'HACCP-Certified', desc: 'Facility meets HACCP standards for food safety. Required for wholesale trade on the platform.', icon: 'ri-shield-star-line', color: 'border-l-secondary-500' },
  { label: 'Export-Grade', desc: 'Meets international export standards including EU and US FDA requirements. Unlocks premium pricing and cross-border trade.', icon: 'ri-medal-line', color: 'border-l-accent-500' },
];

export default function EarningSection() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section className="py-20 md:py-28 warm-sand-bg">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-4">
            Revenue Model
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-700 mb-4">
            How Warehouse Partners Earn
          </h2>
          <p className="text-foreground-500 max-w-xl mx-auto">
            Three transparent revenue streams that turn your freezing capacity into a predictable income engine.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {earningTiers.map((tier) => (
            <div key={tier.label} className="group bg-card-50 border border-card-200 rounded-xl p-6 md:p-8 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 card-animate">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-5 animate-icon-float icon-hover-pop">
                <i className={`${tier.icon} text-xl`}></i>
              </div>
              <h3 className="font-heading font-bold text-primary-700 text-base mb-3">{tier.label}</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">{tier.desc}</p>
            </div>
          ))}
        </div>

        {/* Certification tiers */}
        <div>
          <h3 className="font-heading text-xl font-bold text-primary-700 text-center mb-8">
            Certification Trust Tiers
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {certTiers.map((cert) => (
              <div key={cert.label} className={`bg-card-50 rounded-xl p-5 border border-card-200 border-l-2 ${cert.color} shadow-sm hover:shadow-sm hover:-translate-y-0.5 transition-all duration-200 card-animate`}>
                <div className="flex items-center gap-3 mb-2">
                  <i className={`${cert.icon} text-xl text-primary-600`}></i>
                  <span className="font-heading font-bold text-sm text-primary-700">{cert.label}</span>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed">{cert.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}